import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect } from 'react';
import * as api from '../services/api';

export default function Settings({ showToast, currentUser, editMode }) {
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [tab, setTab] = useState('users');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const { data } = await api.getUsers();
      setUsers(data || []);
    } catch (err) { showToast('Failed to load users'); }
    finally { setLoading(false); }
  }

  async function loadAudit() {
    try { const { data } = await api.getAuditLog({}); setAuditLog(data || []); }
    catch (err) { showToast('Failed to load audit log'); }
  }

  async function changeRole(userId, newRole) {
    try { await api.updateUserRole(userId, newRole); showToast('Role updated'); loadData(); }
    catch (err) { showToast('Update failed: ' + (err.response?.data?.error || 'Insufficient permissions')); }
  }

  const TABS = [
    { id:'users', label:'User Management' },
    { id:'sla', label:'SLA Configuration' },
    { id:'audit', label:'Audit Log' },
    { id:'about', label:'About' },
  ];

  const ROLES = ['requesting_staff','legal_officer','head_of_legal','company_secretary','legal_admin','procurement_officer','board_member','it_admin'];

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="section-header"><div className="section-title">⚙️ System Settings</div></div>

      <div className="tab-bar">
        {TABS.map(t => <div key={t.id} className={`tab ${tab===t.id?'active':''}`} onClick={()=>{setTab(t.id); if(t.id==='audit') loadAudit();}}>{t.label}</div>)}
      </div>

      {tab === 'users' && (
        <div className="card">
          <div className="card-title">👥 Registered Users ({users.length})</div>
          {editMode ? (
            <EditableDataTable
              columns={[{key:'name',label:'Name'},{key:'email',label:'Email'},{key:'department',label:'Department'},{key:'role',label:'Role'},{key:'is_active',label:'Status'}]}
              data={users}
              editable={true}
              onDataChange={(updated) => setUsers(updated)}
              renderCell={(row, colKey) => {
                if (colKey === 'role') return <span className="badge badge-blue">{row.role?.replace(/_/g,' ')}</span>;
                if (colKey === 'is_active') return <span className={`badge ${row.is_active?'badge-green':'badge-red'}`}>{row.is_active?'Active':'Inactive'}</span>;
                return row[colKey] ?? '-';
              }}
            />
          ) : (
          <table><thead><tr><th>Name</th><th>Email</th><th>Department</th><th>Role</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>{users.map(u => (
            <tr key={u.id}>
              <td style={{fontWeight:600}}>{u.name}</td>
              <td>{u.email}</td>
              <td>{u.department||'-'}</td>
              <td>
                <select value={u.role} onChange={e=>changeRole(u.id, e.target.value)} style={{fontSize:11, padding:'3px 6px', borderRadius:6, border:'1px solid var(--border)'}}>
                  {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
                </select>
              </td>
              <td><span className={`badge ${u.is_active?'badge-green':'badge-red'}`}>{u.is_active?'Active':'Inactive'}</span></td>
              <td><button className="sig-btn ghost">Edit</button></td>
            </tr>
          ))}</tbody></table>
          )}
          <div style={{marginTop:12, fontSize:12, color:'var(--muted)'}}>Users are automatically provisioned from Azure AD on first login. Roles can be changed here by IT Admin or Head of Legal.</div>
        </div>
      )}

      {tab === 'sla' && (
        <div className="card">
          <div className="card-title">⏱ SLA Configuration (from SRS Appendix C)</div>
          <table><thead><tr><th>Request Type</th><th>Target (Business Days)</th><th>Escalation Threshold</th></tr></thead>
          <tbody>
            {[
              ['NDA (standard)', 3, 5], ['Full Contract Draft', 7, 10], ['LOI / MOU', 2, 4],
              ['Employment Contract', 5, 7], ['Legal Opinion', 5, 7], ['Document Review', 3, 5],
              ['ITT Qualification', 5, 7], ['Signature Request', 1, 2],
            ].map(([type, target, esc], i) => (
              <tr key={i}><td>{type}</td><td>{target} days</td><td>{esc} days</td></tr>
            ))}
          </tbody></table>
          <div style={{marginTop:12, fontSize:12, color:'var(--muted)'}}>SLA values are configured in the database (sla_config table). Contact IT to modify thresholds.</div>
        </div>
      )}

      {tab === 'audit' && (
        <div className="card">
          <div className="card-title">📋 Audit Log (Last 50 entries)</div>
          <table><thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Entity</th><th>IP Address</th></tr></thead>
          <tbody>{auditLog.slice(0,50).map(a => (
            <tr key={a.id}>
              <td>{new Date(a.timestamp).toLocaleString('en-GB')}</td>
              <td>{a.user_name||'System'}</td>
              <td><span className="badge badge-blue">{a.action}</span></td>
              <td>{a.entity_type||'-'}</td>
              <td style={{fontFamily:'monospace', fontSize:11}}>{a.ip_address||'-'}</td>
            </tr>
          ))}</tbody></table>
          {auditLog.length===0 && <div style={{padding:20, textAlign:'center', color:'var(--muted)'}}>No audit entries yet</div>}
        </div>
      )}

      {tab === 'about' && (
        <div className="card">
          <div className="card-title">ℹ️ About</div>
          <div style={{fontSize:13, lineHeight:1.8}}>
            <p><strong>DEL Legal Service Desk Platform</strong></p>
            <p>Version 1.0.0</p>
            <p>Developed for Desicon Engineering Limited</p>
            <p>Document Ref: DEL-LEGAL-SRS-001 v1.1</p>
            <p style={{marginTop:12}}>This platform implements 79 functional requirements covering: Service Request Management, Contract Drafting, Document Review, ITT Qualification, Digital Signatures, Agreement Lifecycle Management, Case Management, Compliance Tracking, Board & Meeting Governance, and Reporting.</p>
            <p style={{marginTop:8, color:'var(--muted)'}}>© {new Date().getFullYear()} Desicon Engineering Limited. All rights reserved.</p>
          </div>
        </div>
      )}
    </div>
  );
}
